import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'

// configs
import configs from '@/config'

// Load the replay integration lazily (its own async chunk) so the heavy replay
// recorder stays out of the initial parse. See services/sentryReplay.js.
const loadReplayModule = () => import(/* webpackChunkName: "sentry-replay" */ '@/services/sentryReplay')

// Run a callback once the browser is idle, so deferring replay never competes
// with first paint. Falls back to a short timeout where requestIdleCallback is
// unavailable (e.g. Safari).
const runWhenIdle = (cb) => {
  // No window in Jest/SSR — skip scheduling so we don't leak an open timer
  // handle or pull in the replay chunk in a non-browser environment.
  if (typeof window === 'undefined') return
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb, { timeout: 5000 })
  } else {
    setTimeout(cb, 3000)
  }
}

const init = () => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.init({
      release: configs.sentryRelease,
      dsn: configs.sentry,
      environment: configs.env,
      integrations: [
        browserTracingIntegration(),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.2,
      tracesSampleRate: 0.02,
      Vue,
      tracingOptions: {
        trackComponents: true,
      },
      ignoreErrors: [
        'The operation is insecure.',
        'fullscreen error',
        'null is not an object (evaluating \'a.error\')',
        'UET is not defined',
        'too much recursion',
        'Fullscreen request denied',
        'Maximum call stack size exceeded',
        'Unable to decode audio data',
        'Extension context invalidated.',
        'xhr poll error',
        'Cannot read property \'getReadModeConfig\' of undefined',
        '"Timeout"',
        'UnhandledRejection: Non-Error promise rejection captured with value: Timeout',
        'AbortError: The play() request was interrupted by a call to pause().',
        'can\'t access dead object',
        'this.editor is undefined',
        'Cannot read properties of undefined (reading \'setMode\')',
        'undefined is not an object (evaluating \'this.editor.setMode\')',
      ],
      denyUrls: [
        /analytics\.tiktok\.com/i,
        /js\.zohocdn\.com/i,
        /salesiq\.zoho\.com/i,
        /www\.gstatic\.com/i,
        /googletagmanager\.com/i,
        /\/gtm\.js(\?|$)/i,
        /\/gtag\/js(\?|$)/i,
      ],
      // beforeSend helps filtering which issues can go into Sentry
      // filter out all the responses with status equal to 0 (timeout request)
      beforeSend(event, hint) {
        const { originalException: error } = hint

        if (error) {
          if (error.status === 0) return null

          if (error.status >= 400 && error.status < 500) return null
        }

        if (configs.env !== 'production') {
          // log error to browser console
          // for better visibility into exceptions
          if (event?.extra?.error) {
            // eslint-disable-next-line no-console
            console.error(event.extra.error)
          } else if (error) {
            // eslint-disable-next-line no-console
            console.error(error)
          }
        }

        return event
      },
      enableLogs: true,
    })
    // Attach replay after the app is interactive so its recorder chunk loads
    // off the first-paint critical path. Early errors (before it attaches) are
    // still reported — they just won't carry a replay buffer.
    runWhenIdle(() => {
      loadReplayModule().then((m) => m.ensureReplay()).catch(() => {})
    })
  } catch {
    // do nothing
  }
}

const identifyUser = (currentUser) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.setUser(currentUser)
  } catch {
    // do nothing
  }
}

const captureMessage = (message) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureMessage(message)
  } catch {
    // do nothing
  }
}

const captureException = (error) => {
  if (!configs.sentryEnabled) return

  try {
    Sentry.captureException(error)
  } catch {
    // do nothing
  }
}

const startSessionReplay = async () => {
  if (!configs.sentryEnabled) return

  try {
    const m = await loadReplayModule()
    await m.flushReplay()
  } catch (error) {
    captureException(error)
  }
}

const stopSessionReplay = async () => {
  if (!configs.sentryEnabled) return

  try {
    const m = await loadReplayModule()
    await m.stopReplay()
  } catch (error) {
    captureException(error)
  }
}

export default {
  init,
  identifyUser,
  captureMessage,
  captureException,
  startSessionReplay,
  stopSessionReplay,
}
